import React, { useState, useEffect } from 'react';
import { FaHistory, FaSearch, FaUser, FaInfoCircle } from 'react-icons/fa';
import { FiActivity } from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';

export default function AdminHistory() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');

    useEffect(() => {
        const loadLogs = async () => {
            try {
                const data = await fetchAPI('/admin/get_activity_log.php');
                setLogs(data);
            } catch (err) {
                console.error("Failed to load activity logs", err);
            } finally {
                setLoading(false);
            }
        };
        loadLogs();
    }, []);

    const filteredLogs = logs.filter(log =>
        (log.user_name?.toLowerCase() || 'system').includes(filter.toLowerCase()) ||
        log.action.toLowerCase().includes(filter.toLowerCase()) ||
        log.details?.toLowerCase().includes(filter.toLowerCase())
    );

    const getActionColor = (action) => {
        if (action.includes('LOGIN')) return 'bg-blue-100 text-blue-700';
        if (action.includes('APPROVED') || action.includes('CREATED')) return 'bg-green-100 text-green-700';
        if (action.includes('REJECTED') || action.includes('DELETED')) return 'bg-red-100 text-red-700';
        return 'bg-gray-100 text-gray-700';
    };

    const formatDevice = (ua) => {
        if (!ua) return 'Unknown';
        if (ua.includes('Windows')) return 'Windows PC';
        if (ua.includes('iPhone')) return 'iPhone';
        if (ua.includes('Android')) return 'Android Device';
        if (ua.includes('Macintosh')) return 'MacBook';
        return 'Other Device';
    };

    if (loading) return <div className="p-8 text-center text-gray-500 font-bold">Loading history...</div>;

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                    <FaHistory className="text-blue-600" /> System Activity Log
                </h2>
                <div className="relative w-full md:w-80">
                    <input
                        type="text"
                        placeholder="Search logs..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none font-medium"
                    />
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-100">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">User</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Action</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Details</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Device</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">IP Address</th>
                                <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Timestamp</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-50">
                            {filteredLogs.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-400 font-medium italic">No activity logs found</td>
                                </tr>
                            ) : filteredLogs.map((log) => (
                                <tr key={log.id} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                                                <FaUser size={12} />
                                            </div>
                                            <div>
                                                <div className="text-sm font-bold text-gray-900">{log.user_name || 'System'}</div>
                                                <div className="text-[10px] text-gray-400 font-black uppercase tracking-wider">{log.user_role || 'N/A'}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${getActionColor(log.action)}`}>
                                            {log.action.replace(/_/g, ' ')}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-xs text-gray-600 font-medium max-w-xs truncate" title={log.details}>
                                            {log.details || 'No additional details'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1">
                                            <FiActivity size={10} className="text-gray-300" /> {formatDevice(log.user_agent)}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-[10px] font-mono text-gray-400">{log.ip_address}</div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="text-xs text-gray-500 font-medium">
                                            {new Date(log.created_at).toLocaleString()}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
